import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DialogHeader, DialogTitle } from '../ui/dialog';
import { ResponsiveSheet } from '../ui/responsive-sheet';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { resolveActionDevice, sceneControls } from '../../lib/sceneActions';

/**
 * Per-action editor for a scene: the device is already chosen (added via the
 * multi-select picker), so this only sets the TARGET STATE using capability-
 * aware controls — Switch / segmented / slider — and emits a typed payload.
 * No device dropdown, no free-text values.
 */
const DeviceActionEditor = ({ open, action, devices, onSave, onClose }) => {
  const device = useMemo(
    () => resolveActionDevice(action, devices),
    [devices, action],
  );
  // Scene-settable controls only: actuation, de-duplicated, no config settings.
  const controls = useMemo(() => sceneControls(device || {}), [device]);

  // Seed each control from the action's saved payload, else its live value.
  // `seed` is remembered so save can tell what the user actually changed.
  const [values, setValues] = useState({});
  const seedRef = useRef({ payload: {}, seed: {} });
  useEffect(() => {
    if (!open || !device) return;
    const seed = {};
    controls.forEach((c) => {
      seed[c.property] = action?.payload?.[c.property] ?? c.value;
    });
    setValues(seed);
    seedRef.current = { payload: action?.payload || {}, seed };
  }, [open, device, action, controls]);

  const setValue = (property, value) => setValues((prev) => ({ ...prev, [property]: value }));

  const handleSave = () => {
    if (!device) return;
    const { payload: original, seed } = seedRef.current;
    const payload = {};
    controls.forEach((c) => {
      let v = values[c.property];
      if (v === undefined || v === null) return;
      // Only persist keys already in the action or ones the user actually
      // changed — opening + closing must not silently expand a 1-key action.
      const inOriginal = Object.prototype.hasOwnProperty.call(original, c.property);
      if (!inOriginal && v === seed[c.property]) return;
      if (c.valueType === 'binary') {
        const on = v === true || v === 'ON' || v === c.options?.on;
        v = on ? (c.options?.on ?? 'ON') : (c.options?.off ?? 'OFF');
      }
      payload[c.property] = v;
    });
    // Canonical child-id shape (matches new actions + the save-time normalize).
    onSave({
      type: 'device',
      device: device.id,
      parentId: device.parentId || device.id,
      payload,
    });
  };

  const renderControl = (c) => {
    const value = values[c.property];

    if (c.valueType === 'binary') {
      const isOn = value === true || value === 'ON' || value === c.options?.on;
      return (
        <div key={c.property} className="flex items-center justify-between py-3">
          <span className="text-sm font-medium">{c.name || c.property}</span>
          <Switch
            checked={isOn}
            onCheckedChange={(next) => setValue(c.property, next ? (c.options?.on ?? 'ON') : (c.options?.off ?? 'OFF'))}
          />
        </div>
      );
    }

    if (c.type === 'enum' && Array.isArray(c.options?.values)) {
      return (
        <div key={c.property} className="py-3">
          <p className="text-sm font-medium mb-2">{c.name || c.property}</p>
          <div className="flex flex-wrap gap-2">
            {c.options.values.map((opt) => (
              <Button
                key={opt}
                size="sm"
                variant={value === opt ? 'default' : 'outline'}
                onClick={() => setValue(c.property, opt)}
                className="text-xs"
              >
                {opt}
              </Button>
            ))}
          </div>
        </div>
      );
    }

    if (c.valueType === 'numeric' || typeof c.value === 'number') {
      const min = c.options?.min ?? 0;
      const max = c.options?.max ?? 100;
      const step = c.options?.step ?? 1;
      const unit = c.options?.unit || c.unit || '';
      return (
        <div key={c.property} className="py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{c.name || c.property}</span>
            <span className="text-sm font-medium">{value}{unit}</span>
          </div>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value === undefined || value === null ? min : Number(value)}
            onChange={(e) => setValue(c.property, Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
          />
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveSheet open={open} onClose={onClose} size="md">
        <DialogHeader className="shrink-0 px-10 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-lg font-bold text-center truncate">
            {device?.name || device?.friendly_name || 'Device'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-2 divide-y divide-border/10">
          {controls.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">This device has no controllable state.</p>
          ) : (
            controls.map(renderControl)
          )}
        </div>

        <div className="shrink-0 p-4 border-t border-border">
          <Button className="w-full h-11" onClick={handleSave}>Done</Button>
        </div>
    </ResponsiveSheet>
  );
};

export default DeviceActionEditor;
